/**
 * ActiveUsersCard Component
 * Displays DAU/WAU/MAU metrics with trends
 *
 * Story 3.5: Active Users Metrics
 */

import React from 'react';
import { clsx } from 'clsx';
import { Users, UserCheck, Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, Badge } from '../../common';
import { ScopeBadge } from '../ReportScopeIndicator';
import type { ResolvedScope } from '../../../hooks/useReportData';

export interface ActiveUsersData {
  dau: number;
  wau: number;
  mau: number;
  dauTrend: number;
  wauTrend: number;
  mauTrend: number;
  dauWauRatio: number;
  wauMauRatio: number;
  totalUsers: number;
  activeRate: number;
}

export interface ActiveUsersCardProps {
  /** Active users data */
  data: ActiveUsersData;
  /** Loading state */
  isLoading?: boolean;
  /**
   * Resolved scope for THESE figures. A scoped caller gets `organization` here,
   * not `team`: DAU/WAU/MAU are meaningless at team size. The badge stops this
   * card inheriting the page's team label.
   */
  scope?: ResolvedScope;
  /** Additional class names */
  className?: string;
}

/**
 * Get trend color
 */
function getTrendColor(trend: number): string {
  if (trend > 0) return 'text-green-600';
  if (trend < 0) return 'text-red-600';
  return 'text-stone-500';
}

/**
 * Get ratio health color
 */
function getRatioColor(ratio: number): string {
  if (ratio >= 0.4) return 'text-green-600 bg-green-50';
  if (ratio >= 0.2) return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
}

/**
 * Loading skeleton
 */
const ActiveUsersSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-6">
    <div className="grid grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-4 bg-stone-50 rounded-lg">
          <div className="h-8 bg-stone-200 rounded w-16 mb-2" />
          <div className="h-3 bg-stone-200 rounded w-12 mb-1" />
          <div className="h-3 bg-stone-200 rounded w-20" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-2 gap-4">
      {[1, 2].map((i) => (
        <div key={i} className="p-4 bg-stone-50 rounded-lg">
          <div className="h-4 bg-stone-200 rounded w-24 mb-2" />
          <div className="h-6 bg-stone-200 rounded w-16" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Active Users Card Component
 */
export const ActiveUsersCard: React.FC<ActiveUsersCardProps> = ({
  data,
  isLoading = false,
  scope,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Active Users</h3>
        <ActiveUsersSkeleton />
      </Card>
    );
  }

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
      <h3 className="text-lg font-semibold text-stone-900">Active Users</h3>
      <div className="flex items-center gap-2">
        {scope && <ScopeBadge scope={scope} />}
        <Badge variant="secondary" size="sm">
          {data.totalUsers} total users
        </Badge>
      </div>
    </div>
  );

  // No users to measure: every rate below would be 0% over a zero denominator,
  // which reads as "nobody is active" rather than "there is nothing to measure".
  if (data.totalUsers === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        {header}
        <div className="text-center py-8 text-stone-500">
          <Users className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>No users to measure activity for in this period</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      {header}

      {/* DAU/WAU/MAU Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {/* DAU */}
        <div className="p-4 bg-primary-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <UserCheck className="w-5 h-5 text-primary-600" />
            <span className="text-2xl font-bold text-primary-700">{data.dau}</span>
          </div>
          <p className="text-sm text-primary-600 mb-1">Daily Active</p>
          <div className={clsx('flex items-center gap-1 text-xs', getTrendColor(data.dauTrend))}>
            {data.dauTrend > 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : data.dauTrend < 0 ? (
              <TrendingDown className="w-3 h-3" />
            ) : null}
            <span>{data.dauTrend > 0 ? '+' : ''}{data.dauTrend}% vs yesterday</span>
          </div>
        </div>

        {/* WAU */}
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-5 h-5 text-green-600" />
            <span className="text-2xl font-bold text-green-700">{data.wau}</span>
          </div>
          <p className="text-sm text-green-600 mb-1">Weekly Active</p>
          <div className={clsx('flex items-center gap-1 text-xs', getTrendColor(data.wauTrend))}>
            {data.wauTrend > 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : data.wauTrend < 0 ? (
              <TrendingDown className="w-3 h-3" />
            ) : null}
            <span>{data.wauTrend > 0 ? '+' : ''}{data.wauTrend}% vs last week</span>
          </div>
        </div>

        {/* MAU */}
        <div className="p-4 bg-amber-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-5 h-5 text-amber-600" />
            <span className="text-2xl font-bold text-amber-700">{data.mau}</span>
          </div>
          <p className="text-sm text-amber-600 mb-1">Monthly Active</p>
          <div className={clsx('flex items-center gap-1 text-xs', getTrendColor(data.mauTrend))}>
            {data.mauTrend > 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : data.mauTrend < 0 ? (
              <TrendingDown className="w-3 h-3" />
            ) : null}
            <span>{data.mauTrend > 0 ? '+' : ''}{data.mauTrend}% vs last month</span>
          </div>
        </div>
      </div>

      {/* Stickiness Ratios */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className={clsx('p-4 rounded-lg', getRatioColor(data.dauWauRatio))}>
          <p className="text-sm font-medium mb-1">DAU/WAU Ratio</p>
          <p className="text-2xl font-bold">{(data.dauWauRatio * 100).toFixed(0)}%</p>
          <p className="text-xs mt-1 opacity-75">Daily stickiness</p>
        </div>
        <div className={clsx('p-4 rounded-lg', getRatioColor(data.wauMauRatio))}>
          <p className="text-sm font-medium mb-1">WAU/MAU Ratio</p>
          <p className="text-2xl font-bold">{(data.wauMauRatio * 100).toFixed(0)}%</p>
          <p className="text-xs mt-1 opacity-75">Weekly stickiness</p>
        </div>
      </div>

      {/* Overall Active Rate */}
      <div className="p-4 bg-stone-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-stone-700">Overall Active Rate</span>
          <span className={clsx(
            'text-sm font-bold',
            data.activeRate >= 70 ? 'text-green-600' :
            data.activeRate >= 40 ? 'text-amber-600' : 'text-red-600'
          )}>
            {data.activeRate}%
          </span>
        </div>
        <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all',
              data.activeRate >= 70 ? 'bg-green-500' :
              data.activeRate >= 40 ? 'bg-amber-500' : 'bg-red-500'
            )}
            style={{ width: `${data.activeRate}%` }}
          />
        </div>
        <p className="text-xs text-stone-500 mt-2">
          {data.mau} of {data.totalUsers} users active this month
        </p>
      </div>
    </Card>
  );
};

export default ActiveUsersCard;
